import { useState } from "react";
import { Check, CreditCard } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CardIssuer } from "@boo/shared";

const OPTIONS: Array<{ value: CardIssuer | null; label: string }> = [
  { value: null, label: "Not a card bill" },
  { value: CardIssuer.KTC, label: "KTC bill" },
  { value: CardIssuer.UOB, label: "UOB bill" },
];

type CardPaymentEditorProps = {
  transactionId: string;
  issuer: CardIssuer | null;
  isMatched: boolean;
  onChange: (id: string, issuer: CardIssuer | null) => void;
  // Labelled trigger for the mobile action strip (unmarked state only).
  triggerLabel?: string;
  triggerClassName?: string;
};

/**
 * Marks a transaction as a credit card bill payment. Matched to an imported
 * statement → excluded from spending (the statement lines are the expenses).
 * Unmatched → still counted as provisional spending until the statement lands.
 */
export default function CardPaymentEditor({
  transactionId,
  issuer,
  isMatched,
  onChange,
  triggerLabel,
  triggerClassName,
}: CardPaymentEditorProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (value: CardIssuer | null) => {
    onChange(transactionId, value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {issuer ? (
          <button
            title={
              isMatched
                ? "Matched to an imported statement — excluded from spending"
                : "Statement not imported yet — counted as spending until it is"
            }
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium cursor-pointer transition-colors ${
              isMatched
                ? "bg-[var(--surface-muted)] text-muted-foreground hover:bg-border"
                : "bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]"
            }`}
          >
            <CreditCard className="w-3 h-3" />
            {isMatched
              ? `${issuer} bill · excluded`
              : `${issuer} bill · provisional`}
          </button>
        ) : (
          <button
            aria-label="Mark as credit card bill payment"
            className={
              triggerClassName ??
              "p-0.5 rounded text-muted-foreground opacity-40 [@media(hover:none)]:opacity-100 hover:opacity-100 hover:text-foreground hover:bg-[var(--surface-muted)] transition-all cursor-pointer touch-manipulation"
            }
          >
            <CreditCard className={triggerLabel ? "w-3.5 h-3.5" : "w-3 h-3"} />
            {triggerLabel}
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        className="p-1.5 rounded-xl min-w-[168px] bg-card border-[0.5px] border-border shadow-popover"
        align="start"
        role="listbox"
        aria-label="Credit card bill payment"
      >
        <div className="text-[10px] tracking-[0.08em] px-2.5 pt-1.5 pb-1 text-muted-foreground">
          credit card bill payment
        </div>
        {OPTIONS.map((opt) => (
          <button
            key={opt.label}
            role="option"
            aria-selected={opt.value === issuer}
            onClick={() => handleSelect(opt.value)}
            className={`w-full flex items-center gap-2.5 text-[13px] font-medium px-2.5 py-1.5 rounded-lg text-left transition-colors text-foreground ${
              opt.value === issuer
                ? "bg-[var(--color-primary-bg)] hover:bg-[var(--color-primary-bg)]"
                : "bg-transparent hover:bg-[var(--surface-muted)]"
            }`}
          >
            {opt.label}
            {opt.value === issuer && (
              <Check className="w-3.5 h-3.5 ml-auto text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
